import React from "react";

import {
  ActionButton,
  BottomActionSheet,
  CancelButton,
  HeaderLabel,
  HeaderSubtitle,
  HeaderTitle,
  SheetCard,
  SheetHeader,
} from "../../../../../components/BottomActionSheet";
import { DropdownAction } from "../../../../../components/Dropdown/types";
import { splitDropdownActions } from "../../../../../components/Dropdown/utils";
import { isLedgerMode, isManualMode } from "../../../utils";
import { InvestmentActionSheetProps } from "./types";

export const InvestmentActionSheet: React.FC<InvestmentActionSheetProps> = ({
  name,
  subtitle,
  isOpen,
  dropdownActions,
  onClose,
  onAddProgress,
  onAddHolding,
  onMoveCash,
  trackingMode,
}) => {
  const isLedger = isLedgerMode(trackingMode);
  const isManual = isManualMode(trackingMode);
  const { regular: manageActions, destructive: destructiveActions } =
    splitDropdownActions(dropdownActions);

  const runAction = (action: () => void) => {
    action();
    onClose();
  };

  const renderAction = (action: DropdownAction, variant?: "destructive") => (
    <ActionButton
      key={action.id}
      type="button"
      $centered
      $variant={variant}
      disabled={action.disabled}
      onClick={() => runAction(action.onClick)}
    >
      {action.label}
    </ActionButton>
  );

  return (
    <BottomActionSheet
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel="Investment actions"
    >
      <SheetCard>
        <SheetHeader $column>
          <HeaderLabel>Investment</HeaderLabel>
          <HeaderTitle>{name}</HeaderTitle>
          {subtitle && <HeaderSubtitle>{subtitle}</HeaderSubtitle>}
        </SheetHeader>
        {isManual && (
          <ActionButton
            type="button"
            $centered
            onClick={() => runAction(onAddProgress)}
          >
            Add entry
          </ActionButton>
        )}
        {/* Keep this list and `LedgerHeader`'s `DesktopActions` plus its overflow, hidden on compact, in step. */}
        {isLedger && (
          <>
            <ActionButton
              type="button"
              $centered
              onClick={() => runAction(onAddHolding)}
            >
              Record trade
            </ActionButton>
            {onMoveCash && (
              <ActionButton
                type="button"
                $centered
                onClick={() => runAction(onMoveCash)}
              >
                Move cash
              </ActionButton>
            )}
          </>
        )}
      </SheetCard>
      {manageActions.length > 0 && (
        <SheetCard>
          {manageActions.map((action) => renderAction(action))}
        </SheetCard>
      )}
      {destructiveActions.length > 0 && (
        <SheetCard>
          {destructiveActions.map((action) =>
            renderAction(action, "destructive"),
          )}
        </SheetCard>
      )}
      <SheetCard>
        <CancelButton type="button" onClick={onClose}>
          Cancel
        </CancelButton>
      </SheetCard>
    </BottomActionSheet>
  );
};
